import React, { forwardRef, useMemo, useState, useCallback } from 'react';
import {
  Autocomplete,
  AutocompleteProps,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Chip,
  InputAdornment,
  Popper,
  Paper,
  ListItem,
  ListItemText,
  ListItemIcon,
  Divider,
  Fade,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { cva, type VariantProps } from 'class-variance-authority';
import { clsx } from 'clsx';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import WorkIcon from '@mui/icons-material/Work';
import BusinessIcon from '@mui/icons-material/Business';
import { SAP_COLORS, SAP_SPACING, SAP_TYPOGRAPHY, SAP_SHADOWS, SAP_TRANSITIONS } from '../../theme/sapDesignSystem';

// Enhanced option interface
export interface SAPAutocompleteOption {
  label: string;
  value: string | number;
  code?: string;
  status?: number;
  disabled?: boolean;
  group?: string;
  description?: string;
  icon?: React.ReactNode;
  metadata?: Record<string, any>;
}

// Component variants using CVA
const autocompleteVariants = cva(
  'transition-all duration-200 ease-in-out',
  {
    variants: {
      variant: {
        outlined: 'border border-gray-300 rounded-md',
        filled: 'bg-gray-50 border-b-2 border-gray-300',
        standard: 'border-b-2 border-gray-300',
      },
      size: {
        xs: 'h-6 text-xs',
        sm: 'h-8 text-sm',
        md: 'h-10 text-base',
        lg: 'h-12 text-lg',
        xl: 'h-14 text-xl',
      },
      state: {
        default: '',
        error: 'border-red-500',
        success: 'border-green-500',
        warning: 'border-yellow-500',
      },
      fullWidth: {
        true: 'w-full',
        false: 'w-auto',
      },
    },
    defaultVariants: {
      variant: 'outlined',
      size: 'md',
      state: 'default',
      fullWidth: true,
    },
  }
);

// Enhanced TypeScript interface
export interface SAPAutocompleteProps extends Omit<AutocompleteProps<SAPAutocompleteOption, boolean, boolean, boolean>, 'renderInput' | 'size' | 'filterOptions' | 'groupBy' | 'getOptionDisabled' | 'getOptionLabel' | 'onInputChange' | 'renderOption'> {
  // Custom props
  variant?: 'outlined' | 'filled' | 'standard';
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  state?: 'default' | 'error' | 'success' | 'warning';
  fullWidth?: boolean;
  
  // Enhanced accessibility
  ariaLabel?: string;
  ariaDescribedBy?: string;
  
  // Loading state
  loading?: boolean;
  loadingText?: string;
  
  // Helper text
  helperText?: string;
  helperTextType?: 'info' | 'error' | 'success' | 'warning';
  label?: string;
  placeholder?: string;
  
  // Icon support
  startIcon?: React.ReactNode;
  endIcon?: React.ReactNode;
  
  // Option rendering
  showOptionDescription?: boolean;
  showOptionIcon?: boolean;
  showOptionCode?: boolean;
  showOptionStatus?: boolean;
  
  // Grouping
  groupBy?: (option: SAPAutocompleteOption) => string;
  groupLabelRenderer?: (group: string) => React.ReactNode;
  
  // Filtering
  filterOptions?: (options: SAPAutocompleteOption[], params: { inputValue: string }) => SAPAutocompleteOption[];
  highlightMatches?: boolean;
  
  // Validation
  validateOnBlur?: boolean;
  validateOnChange?: boolean;
  required?: boolean;
  
  // Custom styling
  customStyles?: React.CSSProperties;
  optionHeight?: number;
  maxOptions?: number;
  
  // Callbacks
  onValidationChange?: (isValid: boolean, errors: string[]) => void;
  onStateChange?: (state: 'default' | 'error' | 'success' | 'warning') => void;
  onOptionSelect?: (option: SAPAutocompleteOption) => void;
  onInputChange?: (value: string) => void;
}

// Styled components
const StyledAutocomplete = styled(Autocomplete, {
  shouldForwardProp: (prop) => 
    !['state', 'loading', 'helperTextType', 'customStyles', 'optionHeight', 'size'].includes(prop as string),
})<SAPAutocompleteProps>(({ theme, state = 'default', loading, helperTextType, customStyles, optionHeight = 48 }) => ({
  '& .MuiOutlinedInput-root': {
    backgroundColor: SAP_COLORS.sapWhite,
    borderRadius: SAP_SPACING.component.borderRadius,
    transition: SAP_TRANSITIONS.normal,
    border: `1px solid ${getBorderColor(state)}`,
    fontFamily: SAP_TYPOGRAPHY.fontFamily.primary,
    fontSize: SAP_TYPOGRAPHY.fontSize.md,
    fontWeight: SAP_TYPOGRAPHY.fontWeight.regular,
    
    '&:hover': {
      backgroundColor: SAP_COLORS.hover,
      borderColor: getHoverBorderColor(state),
      transform: 'translateY(-1px)',
      boxShadow: SAP_SHADOWS.sm,
    },
    
    '&.Mui-focused': {
      backgroundColor: SAP_COLORS.sapWhite,
      borderColor: getFocusBorderColor(state),
      boxShadow: `0 0 0 2px ${getFocusShadowColor(state)}`,
      transform: 'translateY(-1px)',
    },
    
    '&.Mui-disabled': {
      backgroundColor: SAP_COLORS.disabled,
      borderColor: SAP_COLORS.sapGray3,
      color: SAP_COLORS.sapGray4,
      cursor: 'not-allowed',
      transform: 'none',
      boxShadow: 'none',
    },
    
    '&.Mui-error': {
      borderColor: SAP_COLORS.sapRed,
      '&:hover': {
        borderColor: SAP_COLORS.sapRed,
      },
      '&.Mui-focused': {
        borderColor: SAP_COLORS.sapRed,
        boxShadow: `0 0 0 2px ${SAP_COLORS.sapLightRed}`,
      },
    },
  },
  
  '& .MuiOutlinedInput-input': {
    color: SAP_COLORS.sapGray7,
    fontWeight: SAP_TYPOGRAPHY.fontWeight.regular,
    fontSize: SAP_TYPOGRAPHY.fontSize.md,
    padding: SAP_SPACING.component.padding,
    lineHeight: SAP_TYPOGRAPHY.lineHeight.normal,
    
    '&::placeholder': {
      color: SAP_COLORS.sapGray4,
      opacity: 1,
      fontStyle: 'italic',
    },
    
    ...(loading && {
      color: SAP_COLORS.sapGray4,
      cursor: 'wait',
    }),
  },
  
  '& .MuiInputLabel-root': {
    color: SAP_COLORS.sapGray5,
    fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
    fontSize: SAP_TYPOGRAPHY.fontSize.sm,
    fontFamily: SAP_TYPOGRAPHY.fontFamily.primary,
    
    '&.Mui-focused': {
      color: getFocusLabelColor(state),
      fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold,
    },
    
    '&.Mui-error': {
      color: SAP_COLORS.sapRed,
    },
  },
  
  '& .MuiAutocomplete-popupIndicator': {
    color: SAP_COLORS.sapGray5,
    transition: SAP_TRANSITIONS.fast,
    
    '&:hover': {
      backgroundColor: SAP_COLORS.hover,
      color: SAP_COLORS.sapBlue,
    },
  },
  
  '& .MuiAutocomplete-clearIndicator': {
    color: SAP_COLORS.sapGray5,
    transition: SAP_TRANSITIONS.fast,
    
    '&:hover': {
      backgroundColor: SAP_COLORS.sapLightRed,
      color: SAP_COLORS.sapRed,
    },
  },
  
  '& .MuiAutocomplete-tag': {
    backgroundColor: SAP_COLORS.sapLightBlue,
    color: SAP_COLORS.sapBlue,
    border: `1px solid ${SAP_COLORS.sapBlue}`,
    borderRadius: SAP_SPACING.component.borderRadius,
    fontSize: SAP_TYPOGRAPHY.fontSize.sm,
    fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
    margin: SAP_SPACING.xs,
    
    '& .MuiChip-deleteIcon': {
      color: SAP_COLORS.sapBlue,
      '&:hover': {
        color: SAP_COLORS.sapRed,
      },
    },
  },
  
  ...customStyles,
}));

// Custom Popper component
const StyledPopper = styled(Popper)(({ theme }) => ({
  '& .MuiAutocomplete-paper': {
    backgroundColor: SAP_COLORS.sapWhite,
    borderRadius: SAP_SPACING.component.borderRadius,
    border: `1px solid ${SAP_COLORS.sapGray3}`,
    boxShadow: SAP_SHADOWS.lg,
    marginTop: SAP_SPACING.xs,
    maxHeight: '300px',
    overflow: 'hidden',
    
    '& .MuiAutocomplete-listbox': {
      padding: 0,
      
      '& .MuiAutocomplete-option': {
        minHeight: '48px',
        padding: `${SAP_SPACING.sm} ${SAP_SPACING.md}`,
        borderBottom: `1px solid ${SAP_COLORS.sapGray2}`,
        transition: SAP_TRANSITIONS.fast,
        
        '&:hover': {
          backgroundColor: SAP_COLORS.hover,
        },
        
        '&.Mui-focused': {
          backgroundColor: SAP_COLORS.sapLightBlue,
          color: SAP_COLORS.sapBlue,
        },
        
        '&.Mui-selected': {
          backgroundColor: SAP_COLORS.sapBlue,
          color: SAP_COLORS.sapWhite,
          
          '&:hover': {
            backgroundColor: SAP_COLORS.sapDarkBlue,
          },
        },
        
        '&.Mui-disabled': {
          backgroundColor: SAP_COLORS.disabled,
          color: SAP_COLORS.sapGray4,
          cursor: 'not-allowed',
        },
      },
    },
  },
}));

// Group header component
const GroupHeader = styled(Box)(({ theme }) => ({
  backgroundColor: SAP_COLORS.sapGray1,
  padding: `${SAP_SPACING.sm} ${SAP_SPACING.md}`,
  borderBottom: `1px solid ${SAP_COLORS.sapGray3}`,
  fontWeight: SAP_TYPOGRAPHY.fontWeight.semibold,
  fontSize: SAP_TYPOGRAPHY.fontSize.sm,
  color: SAP_COLORS.sapGray6,
  textTransform: 'uppercase',
  letterSpacing: SAP_TYPOGRAPHY.letterSpacing.wide,
}));

// Utility functions
function getBorderColor(state: string): string {
  switch (state) {
    case 'error': return SAP_COLORS.sapRed;
    case 'success': return SAP_COLORS.sapGreen;
    case 'warning': return SAP_COLORS.sapOrange;
    default: return SAP_COLORS.sapGray3;
  }
}

function getHoverBorderColor(state: string): string {
  switch (state) {
    case 'error': return SAP_COLORS.sapRed;
    case 'success': return SAP_COLORS.sapGreen;
    case 'warning': return SAP_COLORS.sapOrange;
    default: return SAP_COLORS.sapBlue;
  }
}

function getFocusBorderColor(state: string): string {
  switch (state) {
    case 'error': return SAP_COLORS.sapRed;
    case 'success': return SAP_COLORS.sapGreen;
    case 'warning': return SAP_COLORS.sapOrange;
    default: return SAP_COLORS.sapBlue;
  }
}

function getFocusShadowColor(state: string): string {
  switch (state) {
    case 'error': return SAP_COLORS.sapLightRed;
    case 'success': return SAP_COLORS.sapLightGreen;
    case 'warning': return SAP_COLORS.sapLightOrange;
    default: return SAP_COLORS.sapLightBlue;
  }
}

function getFocusLabelColor(state: string): string {
  switch (state) {
    case 'error': return SAP_COLORS.sapRed;
    case 'success': return SAP_COLORS.sapGreen;
    case 'warning': return SAP_COLORS.sapOrange;
    default: return SAP_COLORS.sapBlue;
  }
}

// Default option renderer
const DefaultOptionRenderer = (
  props: any,
  option: SAPAutocompleteOption,
  showDescription: boolean = false,
  showIcon: boolean = false,
  showCode: boolean = false,
  showStatus: boolean = false
) => (
  <ListItem {...props} sx={{ padding: 0 }}>
    {showIcon && (
      <ListItemIcon sx={{ minWidth: 40 }}>
        {option.icon || getDefaultIcon(option)}
      </ListItemIcon>
    )}
    <ListItemText
      primary={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: SAP_TYPOGRAPHY.fontWeight.medium,
              color: 'inherit',
            }}
          >
            {option.label}
          </Typography>
          {showCode && option.code && (
            <Chip
              label={option.code}
              size="small"
              sx={{
                fontSize: SAP_TYPOGRAPHY.fontSize.xs,
                height: '20px',
                backgroundColor: SAP_COLORS.sapGray2,
                color: SAP_COLORS.sapGray6,
              }}
            />
          )}
          {showStatus && option.status !== undefined && (
            <Box
              sx={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: getStatusColor(option.status),
              }}
            />
          )}
        </Box>
      }
      secondary={
        showDescription && option.description ? (
          <Typography
            variant="caption"
            sx={{
              color: SAP_COLORS.sapGray5,
              fontSize: SAP_TYPOGRAPHY.fontSize.xs,
              lineHeight: SAP_TYPOGRAPHY.lineHeight.tight,
            }}
          >
            {option.description}
          </Typography>
        ) : null
      }
    />
  </ListItem>
);

// Default icon getter
function getDefaultIcon(option: SAPAutocompleteOption): React.ReactNode {
  if (option.group === 'client') return <BusinessIcon />;
  if (option.group === 'job') return <WorkIcon />;
  if (option.group === 'user') return <PersonIcon />;
  return <SearchIcon />;
}

// Status color getter
function getStatusColor(status: number): string {
  switch (status) {
    case 1: return SAP_COLORS.sapGreen;
    case 2: return SAP_COLORS.sapOrange;
    case 3: return SAP_COLORS.sapRed;
    default: return SAP_COLORS.sapGray4;
  }
}

// Main component
export const SAPAutocomplete = forwardRef<HTMLDivElement, SAPAutocompleteProps>(
  (
    {
      variant = 'outlined',
      size = 'md',
      state = 'default',
      fullWidth = true,
      loading = false,
      loadingText = 'Loading options...',
      helperText,
      helperTextType = 'info',
      startIcon,
      endIcon,
      showOptionDescription = false,
      showOptionIcon = false,
      showOptionCode = false,
      showOptionStatus = false,
      groupBy,
      groupLabelRenderer,
      filterOptions,
      highlightMatches = true,
      validateOnBlur = true,
      validateOnChange = false,
      required = false,
      customStyles,
      optionHeight = 48,
      maxOptions = 50,
      onValidationChange,
      onStateChange,
      onOptionSelect,
      onInputChange,
      options = [],
      value,
      onChange,
      ...props
    },
    ref
  ) => {
    // Local state
    const [inputValue, setInputValue] = useState('');
    const [isOpen, setIsOpen] = useState(false);

    // Memoized filtered options
    const filteredOptions = useMemo(() => {
      if (!filterOptions) {
        return options.filter(option => !option.disabled).slice(0, maxOptions);
      }
      return filterOptions([...options], { inputValue }).slice(0, maxOptions);
    }, [options, inputValue, filterOptions, maxOptions]);

    // Enhanced filter options with highlighting
    const enhancedFilterOptions = useCallback((options: SAPAutocompleteOption[], params: { inputValue: string }) => {
      const filtered = options.filter(option => !option.disabled);
      return filtered.filter(option =>
        option.label.toLowerCase().includes(params.inputValue.toLowerCase()) ||
        (option.code && option.code.toLowerCase().includes(params.inputValue.toLowerCase())) ||
        (option.description && option.description.toLowerCase().includes(params.inputValue.toLowerCase()))
      );
    }, []);

    // Enhanced option renderer
    const renderOption = useCallback((props: React.HTMLAttributes<HTMLLIElement> & { key: any }, option: SAPAutocompleteOption) => {
      return DefaultOptionRenderer(
        props,
        option,
        showOptionDescription,
        showOptionIcon,
        showOptionCode,
        showOptionStatus
      );
    }, [showOptionDescription, showOptionIcon, showOptionCode, showOptionStatus]);

    // Enhanced group renderer
    const renderGroup = useCallback((params: any) => {
      return (
        <Box key={params.key}>
          <GroupHeader>
            {groupLabelRenderer ? groupLabelRenderer(params.group) : params.group}
          </GroupHeader>
          {params.children}
        </Box>
      );
    }, [groupLabelRenderer]);

    // Enhanced input change handler
    const handleInputChange = useCallback((event: React.SyntheticEvent, value: string, reason: any) => {
      setInputValue(value);
      onInputChange?.(value);
    }, [onInputChange]);

    // Enhanced change handler
    const handleChange = useCallback((event: React.SyntheticEvent, newValue: any) => {
      // Provide default reason to satisfy MUI signature
      (onChange as any)?.(event, newValue, 'selectOption');
      onOptionSelect?.(newValue);
    }, [onChange, onOptionSelect]);

    // Enhanced accessibility props
    const accessibilityProps = {
      'aria-label': props['aria-label'] || 'Autocomplete input',
      'aria-describedby': props['aria-describedby'],
      'aria-busy': loading,
      'aria-expanded': isOpen,
      'aria-haspopup': 'listbox',
    };

    return (
      <Box
        ref={ref}
        sx={{
          position: 'relative',
          width: fullWidth ? '100%' : 'auto',
        }}
      >
        {/** Cast to any to align MUI Autocomplete generics with our option type */}
        {/** eslint-disable-next-line @typescript-eslint/no-explicit-any */}
        <StyledAutocomplete as={undefined as any}
          variant={variant}
          state={state}
          fullWidth={fullWidth}
          loading={loading}
          helperTextType={helperTextType}
          customStyles={customStyles}
          optionHeight={optionHeight}
          options={filteredOptions}
          value={value}
          onChange={handleChange as any}
          inputValue={inputValue}
          onInputChange={handleInputChange as any}
          onOpen={() => setIsOpen(true)}
          onClose={() => setIsOpen(false)}
          filterOptions={enhancedFilterOptions as any}
          renderOption={renderOption as any}
          renderGroup={groupBy ? renderGroup : undefined}
          groupBy={groupBy as any}
          loadingText={loadingText}
          noOptionsText="No options available"
          getOptionLabel={(option: any) => {
            if (typeof option === 'string') return option;
            return option.label || '';
          }}
          getOptionDisabled={(option: any) => (typeof option === 'string' ? false : option.disabled || false)}
          // rely on default identity equality for options
          renderInput={(params) => (
            <TextField
              {...params}
              label={props.label as string}
              placeholder={props.placeholder as string}
              required={required}
              error={state === 'error'}
              helperText={helperText}
              InputProps={{
                ...params.InputProps,
                startAdornment: startIcon ? (
                  <InputAdornment position="start">
                    {startIcon}
                  </InputAdornment>
                ) : params.InputProps?.startAdornment,
                endAdornment: (
                  <>
                    {loading && (
                      <InputAdornment position="end">
                        <CircularProgress size={16} />
                      </InputAdornment>
                    )}
                    {endIcon && (
                      <InputAdornment position="end">
                        {endIcon}
                      </InputAdornment>
                    )}
                    {params.InputProps?.endAdornment}
                  </>
                ),
              }}
            />
          )}
          componentsProps={{
            popper: {
              component: StyledPopper,
            },
          }}
          disabled={loading || props.disabled}
          {...accessibilityProps}
          {...(props as any)}
        />
      </Box>
    );
  }
);

// Display name for debugging
SAPAutocomplete.displayName = 'SAPAutocomplete';

// Export types for external use
export { autocompleteVariants };
